/**
 * Auto-Fees page: landing fees booked automatically on chosen clients'
 * new incoming bank payments (app/services/auto_fees.py).
 */
import axios, { type AxiosInstance } from "axios";
import { getSessionToken } from "@/lib/session";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

function makeClient(): AxiosInstance {
  const client = axios.create({ baseURL: `${BASE_URL}/api/v1`, timeout: 30_000 });
  client.interceptors.request.use((config) => {
    const token = getSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    try {
      const raw = localStorage.getItem("millbank-impersonation");
      if (raw) {
        const s = JSON.parse(raw)?.state;
        if (s?.target?.id && s?.startedAt) {
          const ageMs = Date.now() - new Date(s.startedAt).getTime();
          if (ageMs < (s.maxDurationSeconds || 3600) * 1000) {
            config.headers["X-Impersonate-User-Id"] = s.target.id;
            config.headers["X-Impersonate-Started-At"] = s.startedAt;
          }
        }
      }
    } catch {
      /* no impersonation */
    }
    return config;
  });
  return client;
}

const api = makeClient();

export interface AutoFeeRule {
  accountId: string; accountName: string; feePct: number; enabled: boolean; activeFrom: string | null;
  createdBy: string | null; createdAt: string | null; updatedBy: string | null; updatedAt: string | null;
}
export type AutoFeeRunStatus = "booking" | "booked" | "failed" | "skipped";
export interface AutoFeeRun {
  bankPaymentId: string; bankPaymentName: string; accountId: string; accountName: string;
  amount: number | null; currency: string; feePct: number | null; fee: number | null; net: number | null;
  status: AutoFeeRunStatus; tradeId: string | null; tradeName: string | null; ticketId: string | null;
  error: string | null; notified: string | null; at: string | null; updatedAt: string | null; retriedBy: string | null;
}
export interface AutoFeesData {
  rules: AutoFeeRule[]; runs: AutoFeeRun[]; status: { lastPollAt: string | null; lastError: string | null };
}

export const autoFeesApi = {
  get: () => api.get<AutoFeesData>("/auto-fees").then((r) => r.data),
  save: (accountId: string, feePct: number, enabled: boolean) =>
    api.put<{ rule: AutoFeeRule }>(`/auto-fees/rules/${accountId}`, { feePct, enabled }).then((r) => r.data.rule),
  remove: (accountId: string) => api.delete(`/auto-fees/rules/${accountId}`).then(() => undefined),
  retry: (bankPaymentId: string) =>
    api.post<{ run: AutoFeeRun }>(`/auto-fees/runs/${bankPaymentId}/retry`, undefined, { timeout: 180_000 }).then((r) => r.data.run),
};

/** The signed-in user's own notification feed (staff bell). */
export interface FeedItem {
  id: string; kind: string; title: string; body: string; deeplink: string;
  accountId: string | null; accountName: string | null; createdAt: string | null; isRead: boolean;
}
export const feedApi = {
  list: () => api.get<{ items: FeedItem[]; unreadCount: number }>("/notifications/feed", { params: { limit: 30 } }).then((r) => r.data),
  read: (id: string) => api.post(`/notifications/feed/${id}/read`).then(() => undefined),
  readAll: () => api.post("/notifications/feed/read-all").then(() => undefined),
};
